import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminHeader } from '@app/components/shared/AdminHeader';
import { initializeSalesforceConfig, getSalesforceUrl, isSalesforceConfigInitialized } from '@shared/utils/salesforceUtils';

interface Contact {
  id: number;
  sfid: string;
  firstname: string;
  lastname: string;
  name: string;
  email: string;
  createddate: string;
  systemmodstamp: string;
}

interface ContactsResponse {
  contacts: Contact[];
  total_count: number;
  limit: number;
  offset: number;
}

interface AdminDashboardProps {
  onLogout: () => void;
}

export function AdminDashboard({ onLogout }: AdminDashboardProps) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [salesforceConfigLoaded, setSalesforceConfigLoaded] = useState(false);
  const navigate = useNavigate();

  const pageSize = 20;

  const fetchContacts = async (offset = 0) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/contacts?limit=${pageSize}&offset=${offset}`, {
        credentials: 'include',
      });

      if (response.status === 401) {
        onLogout();
        navigate('/login');
        return;
      }

      const result = await response.json();
      if (result.success) {
        setContacts(result.data.contacts);
        setTotalCount(result.data.total_count);
        setCurrentPage(Math.floor(offset / pageSize));
      } else {
        setError(result.error || 'Failed to fetch contacts');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
    
    // Initialize Salesforce config
    initializeSalesforceConfig().then(() => {
      setSalesforceConfigLoaded(true);
    });
  }, []);

  const handleEdit = (contact: Contact) => {
    setEditingContact({ ...contact });
  };

  const handleSave = async () => {
    if (!editingContact) return;

    try {
      const response = await fetch(`/api/contacts/${editingContact.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          firstname: editingContact.firstname,
          lastname: editingContact.lastname,
          email: editingContact.email,
        }),
      });

      const result = await response.json();
      if (result.success) {
        setContacts(contacts.map(c => c.id === editingContact.id ? result.data : c));
        setEditingContact(null);
      } else {
        setError(result.error || 'Failed to update contact');
      }
    } catch (err) {
      setError('Network error occurred');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this contact?')) return;

    try {
      const response = await fetch(`/api/contacts/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      const result = await response.json();
      if (result.success) {
        setContacts(contacts.filter(c => c.id !== id));
        setTotalCount(totalCount - 1);
      } else {
        setError(result.error || 'Failed to delete contact');
      }
    } catch (err) {
      setError('Network error occurred');
    }
  };



  const nextPage = () => {
    const newOffset = (currentPage + 1) * pageSize;
    if (newOffset < totalCount) {
      fetchContacts(newOffset);
    }
  };

  const prevPage = () => {
    const newOffset = Math.max(0, (currentPage - 1) * pageSize);
    fetchContacts(newOffset);
  };

  if (loading && contacts.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading contacts...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader onLogout={onLogout} />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {error && (
            <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Contacts ({totalCount})
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Manage your Salesforce contacts
              </p>
            </div>

            {contacts.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No contacts found</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {contacts.map((contact) => (
                  <li key={contact.id} className="px-4 py-4 sm:px-6">
                    {editingContact?.id === contact.id ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                          <input
                            type="text"
                            value={editingContact.firstname || ''}
                            onChange={(e) => setEditingContact({
                              ...editingContact,
                              firstname: e.target.value
                            })}
                            placeholder="First Name"
                            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                          />
                          <input
                            type="text"
                            value={editingContact.lastname || ''}
                            onChange={(e) => setEditingContact({
                              ...editingContact,
                              lastname: e.target.value
                            })}
                            placeholder="Last Name"
                            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                          />
                          <input
                            type="email"
                            value={editingContact.email || ''}
                            onChange={(e) => setEditingContact({
                              ...editingContact,
                              email: e.target.value
                            })}
                            placeholder="Email"
                            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                          />
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={handleSave}
                            className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingContact(null)}
                            className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h4 className="text-sm font-medium text-gray-900">
                              {contact.name || `${contact.firstname} ${contact.lastname}`}
                            </h4>
                            <span className="text-sm text-gray-500">
                              {contact.email}
                            </span>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            Created: {new Date(contact.createddate).toLocaleDateString()}
                            {contact.sfid && (
                              <>
                                {' '}• SF ID: {contact.sfid}
                              </>
                            )}
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          {contact.sfid && salesforceConfigLoaded && (
                            <a
                              href={getSalesforceUrl(contact.sfid)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 inline-block text-center"
                            >
                              View in SF
                            </a>
                          )}
                          <button
                            onClick={() => handleEdit(contact)}
                            className="bg-yellow-600 text-white px-3 py-1 rounded text-sm hover:bg-yellow-700"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(contact.id)}
                            className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {totalCount > pageSize && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={prevPage}
                    disabled={currentPage === 0}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={nextPage}
                    disabled={(currentPage + 1) * pageSize >= totalCount}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing{' '}
                      <span className="font-medium">{currentPage * pageSize + 1}</span>
                      {' '}to{' '}
                      <span className="font-medium">
                        {Math.min((currentPage + 1) * pageSize, totalCount)}
                      </span>
                      {' '}of{' '}
                      <span className="font-medium">{totalCount}</span>
                      {' '}results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={prevPage}
                        disabled={currentPage === 0}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={nextPage}
                        disabled={(currentPage + 1) * pageSize >= totalCount}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}